/** The panes the cabinet actually renders — every one of these has a page. */
export type PaneKey = "dash" | "shipments" | "billing" | "rates" | "store" | "myqubics" | "apikeys" | "settings";

export type NavItemKey = PaneKey;

export type SubItem = {
  key: string;
  label: string;
  /** URL segment under the pane. The default sub-item never appears in the path. */
  slug: string;
  /** Kept in the model but out of the menu and out of the router. */
  hidden?: boolean;
};

export type NavItem = {
  key: NavItemKey;
  label: string;
  /** URL segment under /cabinet. Empty for the pane the bare path lands on. */
  slug: string;
  /** Kept in the model but out of the menu and out of the router. */
  hidden?: boolean;
  /**
   * Sub-items are addressable URL tabs drawn inside the pane (stepper), not a
   * second list under the left nav.
   */
  tabsInPane?: boolean;
  sub?: SubItem[];
};

/** A nav item with a pane behind it — what the menu and the router are allowed to see. */
export type VisibleNavItem = NavItem & { key: PaneKey };

/**
 * One `.pc-nav` block in the sidebar: a `.pc-nav__t` heading and the buttons
 * under it. `className` is the design's own modifier, carried so the ported CSS
 * keeps matching (see design/account/account.css).
 */
export type NavGroup = {
  key: string;
  title: string;
  className?: string;
  items: NavItem[];
};

export type VisibleNavGroup = Omit<NavGroup, "items"> & { items: VisibleNavItem[] };

/**
 * The whole cabinet menu, as the design lays it out — four groups, read at
 * `data-role="user"` / `data-scope="user"`. The prototype's API Reselling group
 * and its club-only entries (My Network, Messages, Notifications) belong to
 * roles this cabinet does not serve, so they are not in the model at all.
 *
 * `hidden` marks an entry that exists in the design but has no page behind it
 * yet. It keeps its key and slug — so the work of building it starts from a
 * named place — but stays out of the menu and unreachable by URL, because a
 * menu item that leads nowhere reads as a broken product, not a roadmap.
 *
 * Do not un-hide an entry to "see how it looks". Delete the flag only in the
 * change that gives it a real page, one entry at a time; the pane's fallback
 * card is what it renders in between. Every Shipments sub-item except My
 * Shipments and Tracking is waiting on that.
 *
 * A group is only ever as visible as its items: one whose entries are all
 * hidden drops out of the sidebar heading and all, rather than leaving an empty
 * heading behind.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    key: "overview",
    title: "Overview",
    className: "pc-nav--club",
    items: [
      { key: "dash", label: "Dashboard", slug: "dashboard" },
      {
        key: "rates",
        label: "Logistics Data",
        slug: "logistics-data",
        tabsInPane: true,
        sub: [
          { key: "overview", label: "Overview", slug: "overview" },
          { key: "import", label: "Import & Parse", slug: "import" },
          { key: "library", label: "Data Review", slug: "review" },
          { key: "pricing", label: "Pricing Rules", slug: "pricing" },
          { key: "published", label: "Approve & Publish", slug: "publish" },
        ],
      },
    ],
  },
  {
    key: "platform",
    title: "Qubictron Platform",
    items: [
      {
        key: "shipments",
        label: "Shipments",
        slug: "shipments",
        sub: [
          { key: "shipments", label: "My Shipments", slug: "my-shipments" },
          { key: "tracking", label: "Tracking", slug: "tracking" },
          { key: "demurrage", label: "Demurrage & Storage", slug: "demurrage", hidden: true },
          { key: "schedules", label: "Schedules", slug: "schedules", hidden: true },
          { key: "tools", label: "Platform Qubics", slug: "tools", hidden: true },
          { key: "analytics", label: "Analytics", slug: "analytics", hidden: true },
        ],
      },
      // The design draws this as #nav-platform: a sibling button, not a sub-item.
      { key: "billing", label: "Plan & Billing", slug: "plan-billing" },
    ],
  },
  {
    key: "build",
    title: "Build & Integrate",
    className: "pc-nav--store",
    items: [
      {
        key: "store",
        label: "Qubics Store",
        slug: "qubics-store",
        // The API / Web switch at the top of the pane, addressable so My Qubics
        // can send a visitor to either one.
        tabsInPane: true,
        sub: [
          { key: "api", label: "API & MCP Integration", slug: "api" },
          { key: "web", label: "Web Integration", slug: "web" },
        ],
      },
      { key: "myqubics", label: "My Qubics", slug: "my-qubics" },
      { key: "apikeys", label: "API Keys", slug: "api-keys" },
    ],
  },
  {
    key: "account",
    title: "Account",
    className: "pc-nav--set",
    items: [{ key: "settings", label: "Settings", slug: "settings" }],
  },
];

/** Every entry in the model, in sidebar order. Derived, so it cannot drift from the groups. */
export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((group) => group.items);

/**
 * `hidden` is the only thing standing between an entry and a pane, so this is
 * where the model promises TypeScript that what is left has one.
 */
const isBuilt = (item: NavItem): item is VisibleNavItem => item.hidden !== true;

/** First sub-item of each section — what opening the section lands on. */
export const DEFAULT_SUB: Record<PaneKey, string | null> = {
  dash: null,
  shipments: "shipments",
  billing: null,
  rates: "overview",
  store: "api",
  myqubics: null,
  apikeys: null,
  settings: null,
};

/** What the menu renders and what the address bar can reach. */
export const VISIBLE_NAV_ITEMS: VisibleNavItem[] = NAV_ITEMS.filter(isBuilt);

/**
 * The sidebar for the current user. Logistics Data stays in NAV_GROUPS /
 * VISIBLE_NAV_ITEMS for the model and URL map, but non-admins never get it in
 * the left nav — App redirects them off the pane if they deep-link.
 */
export const visibleNavGroups = (isAdmin: boolean): VisibleNavGroup[] =>
  NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(isBuilt).filter((item) => isAdmin || item.key !== "rates"),
  })).filter((group) => group.items.length > 0);

/** The same entries without their grouping — what the router guards work from. */
export const visibleNavItems = (isAdmin: boolean): VisibleNavItem[] =>
  visibleNavGroups(isAdmin).flatMap((group) => group.items);

/** The sub-items of a pane that are built — the only ones the menu or a URL gets. */
export const visibleSubs = (item: NavItem | undefined): SubItem[] =>
  (item?.sub ?? []).filter((sub) => sub.hidden !== true);

/** Where the cabinet opens when the path names no pane. */
export const HOME_PANE: PaneKey = "dash";

/**
 * Import workflow steps (import | library | pricing | published) use the full-bleed
 * shell: left cabinet nav hidden, content wider. Overview keeps the normal layout.
 * A null/undefined sub resolves as overview (DEFAULT_SUB).
 */
export const isRatesWorkflow = (pane: PaneKey | null, sub: string | null | undefined): boolean =>
  pane === "rates" && (sub ?? DEFAULT_SUB.rates) !== "overview";

export const findNavItem = (pane: PaneKey): NavItem | undefined => NAV_ITEMS.find((item) => item.key === pane);
